// --- Dependencies -------------------------------------------------------------------------------
import { useEffect, useRef, useState } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    url: string | null;
    file: File | null;
    onPick: (file: File | null) => void;
    onRemove: () => void;
    changeLabel: string;
    removeLabel: string;
    placeholder: string;
    shape?: "circle" | "square" | "wide";
    disabled?: boolean;
};

// --- Logic --------------------------------------------------------------------------------------

export default function ImagePicker({ url, file, onPick, onRemove, changeLabel, removeLabel, placeholder, shape = "circle", disabled }: Props) {
    const inputRef = useRef<HTMLInputElement>(null);
    const [filePreview, setFilePreview] = useState<string | null>(null);

    useEffect(() => {
        if (!file) {
            setFilePreview(null);
            return;
        }
        const objectUrl = URL.createObjectURL(file);
        setFilePreview(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [file]);

    // A freshly picked file always wins over whatever is already stored.
    const preview = filePreview ?? url;

    const handlePick = () => inputRef.current?.click();

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onPick(event.target.files?.[0] ?? null);
        // Reset so picking the same file twice still fires a change.
        event.target.value = "";
    };

    return (
        <div className="image-picker">
            <button
                type="button"
                className={`image-picker-preview image-picker-preview--${shape}`}
                onClick={handlePick}
                disabled={disabled}
                aria-label={changeLabel}
            >
                {preview ? (
                    <img src={preview} alt="" />
                ) : (
                    <img className="image-picker-placeholder" src={placeholder} alt="" width={26} height={26} />
                )}
            </button>

            <div className="image-picker-actions">
                <button type="button" className="image-picker-change" onClick={handlePick} disabled={disabled}>
                    {changeLabel}
                </button>
                {preview && (
                    <button type="button" className="image-picker-remove" onClick={onRemove} disabled={disabled}>
                        {removeLabel}
                    </button>
                )}
            </div>

            <input ref={inputRef} type="file" accept="image/*" className="image-picker-native" onChange={handleChange} disabled={disabled} />
        </div>
    );
}
